import type { RouteLocationNormalizedGeneric, RouteLocationRaw } from 'vue-router'

import { Cache } from './services/cache/Cache.ts'

let routerGuardsEnabled = false

export async function routerGuards(route: RouteLocationNormalizedGeneric, next: (value?: string | Error | RouteLocationRaw) => void) {
  if (isOneTimeCase() && !route.fullPath.includes('one-time-case')) {
    // One time case users can only access the one time case page.
    return next(Error('Unauthorized route'))
  }

  if (!stores.user.authenticated && !route.meta.guest) {
    // Non authenticated users that go to non-guest routes need to sign up.
    return { path: '/login', query: { next: route.fullPath } }
  } else if (stores.user.authenticated && route.meta.guest && !route.meta.allowSignedIn) {
    // Authenticated users cannot access guest routes.
    return next(String(route.query?.next ?? '/'))
  }

  // When a user opens a link that needs to be in a certain organization,
  // we can handle the switching to that organization using a query parameter.
  if (route.query.organization_id) {
    const organizationId = Number(route.query.organization_id)
    if (!stores.user.user.organizations.some((organization) => organization.id === organizationId)) {
      return next('/')
    }

    await stores.organization.switch(organizationId)
    await Cache.tags('organization').put(stores.user.user.id, stores.organization.id)
    const query = route.query
    delete query.organization_id

    return next({ path: route.path, query })
  }

  if (route.meta.organizationRequired && !stores.organization.id) {
    return next('/inactive-organisation')
  }

  if (route.meta.permission && !can(route.meta.permission)) {
    return next(Error(`Unauthorized route: missing permission ${route.meta.permission}`))
  }

  if (route.meta.service && !(route.meta.service in stores.services.$state)) {
    return next(Error(`Unauthorized route: missing service ${route.meta.service}`))
  }

  if (route.meta.visible && !route.meta.visible()) {
    return next(Error(`Unauthorized route: not visible ${route.path}`))
  }

  if ((route.meta.app === 'onboarding' || route.path.startsWith('/client-onboarding/')) && !stores.organization.has_onboarding) {
    return next(Error(`Unauthorized route: ${route.path}`))
  }

  if ((route.meta.app === 'transaction_monitoring' || route.path.startsWith('/transaction-monitoring/')) && !stores.organization.has_transaction_monitoring) {
    return next(Error(`Unauthorized route: ${route.path}`))
  }

  // When a user tries to access an organization that they are not in, without explicitly enabled ghost mode or without consent, switch back to an organization that they are in.
  const organizationId = Number(route.params.organizationId ?? stores.organization.id)
  if (
    organizationId &&
    route.meta.organizationRequired &&
    stores.user.authenticated &&
    !route.path.startsWith('/administration/organizations/') &&
    !stores.user.user.organizations.some((organization) => organization.id === organizationId) &&
    (!(await Cache.tags('globals').get('ghostMode')) || !stores.organization.account_access_consent)
  ) {
    if (!route.path.startsWith('/administration/')) {
      $router.replace('/')
    }
    await stores.organization.switch(stores.user.user.organizations[0]?.id)
  }

  return next()
}

export async function checkCurrentRoute() {
  if (!routerGuardsEnabled) {
    return
  }

  await routerGuards($router.currentRoute.value, async (result) => {
    if (result instanceof Error) {
      await $router.replace('/')
    } else if (result) {
      try {
        await $router.replace(result)
      } catch {
        await $router.replace('/')
      }
    }
  })
}
import { userpilotClient } from './services/UserpilotClient.ts'
import type { AppName } from './types.ts'

export const history = ref<string[]>([])

export function createRouterGuards() {
  routerGuardsEnabled = true
  $router.beforeEach(async (to, from, next) => {
    return await routerGuards(to, next)
  })

  $router.afterEach((to, from) => {
    if (from.meta.guest && !to.meta.guest) {
      // When we are logging in or after a password reset, a special transition is used.
      to.meta.transition = 'v-slide-out-right-fade'
    } else {
      to.meta.transition = 'v-fade-fast'
    }

    if (to.meta.title) {
      setDocumentTitle(to.meta.title)
    }

    if (history.value.at(-2) === to.fullPath) {
      // Handle going back to previous page.
      history.value.splice(history.value.length - 1, 1)
    } else {
      history.value.push(to.fullPath)
    }

    userpilotClient.reload()
  })
}

const homeRoutes: Record<AppName, string> = {
  onboarding: '/client-onboarding/home',
  spectrum: '/administration/home',
  transaction_monitoring: '/transaction-monitoring/home',
  screening: '/home',
}

/**
 * Go back in the history, or replace the current route with the fallback route.
 * If the current route is a detail route, the route will be replaced with the list route.
 * @param fallback Fallback route to use if there is no history or the current route is not a detail route.
 */
export async function backWithFallback(fallback?: string) {
  if (history.value.length > 1) {
    $router.back()
  } else if (fallback) {
    await $router.replace(fallback)
  } else if ($route.value.name?.toString().endsWith('.id')) {
    await $router.replace({ name: $route.value.name.toString().slice(0, -3) })
  } else {
    const app: AppName = await Cache.tags('UIState').get('app')
    await $router.replace(homeRoutes[app])
  }
}
